import { create, StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';

export interface DelegationTarget {
  type: 'organization' | 'integrator';
  id: string;
  name: string;
  description?: string;
}

export interface DelegationContext {
  type: 'organization' | 'integrator';
  entityId: string;
  name: string;
}

interface DelegationState {
  // Current delegation context (if any)
  currentDelegation: DelegationContext | null;

  // Available targets user can delegate to
  availableTargets: DelegationTarget[];

  // Actions
  setDelegation: (delegation: DelegationContext | null) => void;
  clearDelegation: () => void;
  setAvailableTargets: (targets: DelegationTarget[]) => void;

  // Computed properties
  isDelegating: boolean;
  delegationParam: string | null;
}

// Store implementation without persist
const storeImplementation: StateCreator<DelegationState> = (set) => ({
  currentDelegation: null,
  availableTargets: [],

  setDelegation: (delegation) => set({
    currentDelegation: delegation,
    isDelegating: delegation !== null,
    delegationParam: delegation ? `${delegation.type}:${delegation.entityId}` : null,
  }),

  clearDelegation: () => set({
    currentDelegation: null,
    isDelegating: false,
    delegationParam: null,
  }),

  setAvailableTargets: (targets) => set({ availableTargets: targets }),

  // Computed properties (updated by setDelegation)
  isDelegating: false,
  delegationParam: null,
});

// Persist options
const persistOptions: PersistOptions<DelegationState, Pick<DelegationState, 'currentDelegation' | 'isDelegating' | 'delegationParam'>> = {
  name: 'delegation-context',
  partialize: (state) => ({
    currentDelegation: state.currentDelegation,
    isDelegating: state.isDelegating,
    delegationParam: state.delegationParam,
  }),
};

// Create store - only use persist on client side
export const useDelegationStore = typeof window !== 'undefined'
  ? create<DelegationState>()(persist(storeImplementation, persistOptions))
  : create<DelegationState>()(storeImplementation);

// Helper hook to get delegation-aware API parameters
export const useDelegationParams = () => {
  const { delegationParam } = useDelegationStore();
  
  return {
    // Get URL search params with delegation if active
    getApiParams: () => {
      const params = new URLSearchParams();
      if (delegationParam) {
        params.set('delegateAs', delegationParam);
      }
      return params;
    },
    
    // Get delegation query parameter for API calls
    getDelegationQuery: () => ({
      ...(delegationParam && { delegateAs: delegationParam }),
    }),
  };
};